import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import { ErrorCode } from '@/shared/api/types';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { LogoSymbol } from '@/shared/ui/Logo';

/**
 * Sessao bloqueada (o /locked do OnPoint).
 *
 * Abrir esta tela com a sessao aberta encerra a sessao no servidor, como no OnPoint:
 * a tela bloqueada nao tem nada vivo por baixo. O e-mail e a empresa ficam guardados
 * na propria tela, e desbloquear e entrar de novo, na mesma empresa, voltando para
 * onde a pessoa estava. Quem nao e a pessoa da sessao entra pelo acesso comum.
 */
export function SessionLockedPage() {
  const { status, session, signIn, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const destino = (location.state as { from?: string } | null)?.from ?? '/';

  // Guardado no primeiro instante: depois do encerramento, a sessao some do contexto.
  const [bloqueio] = useState(() => ({
    email: session?.user.email ?? '',
    empresa: session?.tenant.slug ?? '',
    nome: session?.user.name ?? '',
  }));
  const [email, setEmail] = useState(bloqueio.email);
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const encerrou = useRef(false);

  useEffect(() => {
    if (status === 'authenticated' && !encerrou.current) {
      encerrou.current = true;
      // A falha do encerramento remoto nao mantem a sessao local: o contexto limpa a
      // sessao de qualquer forma.
      signOut().catch(() => undefined);
    }
  }, [status, signOut]);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    void desbloquear();
  }

  async function desbloquear() {
    setErro(null);
    setEnviando(true);
    try {
      await signIn(email, senha, bloqueio.empresa || undefined);
      void navigate(destino, { replace: true });
    } catch (causa) {
      if (causa instanceof ApiError && causa.code === ErrorCode.InvalidCredentials) {
        setErro('Email ou senha incorretos.');
      } else if (causa instanceof ApiError && causa.code === ErrorCode.AccountLocked) {
        setErro(
          'Conta temporariamente bloqueada por tentativas seguidas. Aguarde alguns minutos.',
        );
      } else if (causa instanceof ApiError && causa.code === ErrorCode.RateLimited) {
        setErro('Muitas tentativas em pouco tempo. Aguarde um instante.');
      } else {
        setErro('Nao foi possivel desbloquear. Tente novamente.');
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas px-5 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <LogoSymbol size={48} />
          <h1 className="text-xl font-semibold text-ink-900">
            Sua sessao esta bloqueada
          </h1>
          <p className="text-sm text-ink-500">
            {bloqueio.nome
              ? `${bloqueio.nome}, digite a sua senha para continuar de onde parou.`
              : 'Digite o seu e-mail e a sua senha para continuar.'}
          </p>
        </div>

        <form onSubmit={enviar} noValidate className="flex flex-col gap-4">
          {erro ? <Alert tone="danger">{erro}</Alert> : null}

          <Field
            label="E-mail"
            type="email"
            autoComplete="username"
            required
            value={email}
            readOnly={bloqueio.email !== ''}
            onChange={(evento) => setEmail(evento.target.value)}
            placeholder="Digite seu e-mail"
          />
          <Field
            label="Senha"
            type="password"
            autoComplete="current-password"
            required
            value={senha}
            onChange={(evento) => setSenha(evento.target.value)}
            placeholder="Digite sua senha"
          />

          <Button type="submit" size="lg" loading={enviando} className="mt-2 w-full">
            Desbloquear
          </Button>
        </form>

        <p className="mt-6 text-center text-xs text-ink-500">
          Nao e voce?{' '}
          <Link to="/entrar" className="font-medium text-brand-600 hover:underline">
            Entrar com outra conta
          </Link>
        </p>
      </div>
    </div>
  );
}
